import React, { useState } from "react";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { Store } from "../../../__generated__/graphql";
import { getCDNResizedImageUrl } from "../../../utils/get-cdn-resized-image-url";
import { HiXMark } from "react-icons/hi2";

interface NewStoreNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  stores: Store[];
  onGoToStore: (store: Store) => void;
}

export const NewStoreNotificationModal: React.FC<
  NewStoreNotificationModalProps
> = ({ isOpen, onClose, stores, onGoToStore }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!isOpen || stores.length === 0) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const currentStore = stores[currentIndex];

  const handleGoToStore = () => {
    onGoToStore(currentStore);
  };

  const getDistanceText = (distance: number | null | undefined) => {
    if (!distance && distance !== 0) return "";
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m 앞`;
    }
    return `${distance.toFixed(1)}km 앞`;
  };

  const getTitleText = (distance: number | null | undefined) => {
    if (distance && distance <= 1) {
      return (
        <div className="">
          내 주변{" "}
          <span className="text-[#1562fc] font-extrabold text-4xl">
            {Math.round(distance * 1000)}m
          </span>
          에
          <br />
          <span className="">새로운 맛집이 등장! 🏠</span>
        </div>
      );
    }
    return (
      <>
        따끈따끈 <br /> <span className="text-[#1562fc]">신규 매장</span>
        <span className="font-extrabold ml-1">등장 👀</span>
      </>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black bg-opacity-50 p-6 animate-[fadeIn_0.2s_ease-out]"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-[340px] bg-white rounded-2xl shadow-xl overflow-hidden relative animate-[scaleUp_0.3s_cubic-bezier(0.16,1,0.3,1)] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-gray-100 transition-colors text-gray-500 z-30 backdrop-blur-sm"
        >
          <HiXMark className="w-6 h-6" />
        </button>

        <div className="pt-8 px-6 pb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-2">
            {getTitleText(currentStore.distance)}
          </h2>
          {currentStore.distance && currentStore.distance > 1 ? (
            <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-200 bg-opacity-70 text-gray-700 text-xs font-semibold">
              {getDistanceText(currentStore.distance)}
            </span>
          ) : null}
        </div>

        <div className="w-full relative">
          <Swiper
            modules={[Autoplay]}
            spaceBetween={12}
            slidesPerView={1}
            style={{
              paddingLeft: "42px",
              paddingRight: "42px",
            }}
            centeredSlides={true}
            autoplay={
              stores.length > 1
                ? { delay: 3000, disableOnInteraction: false }
                : false
            }
            onSlideChange={(swiper: SwiperClass) =>
              setCurrentIndex(swiper.activeIndex)
            }
            className="w-full pb-4"
          >
            {stores.map((store) => (
              <SwiperSlide key={store.id} className="px-1">
                <div
                  className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer"
                  onClick={() => {
                    if (currentStore.id === store.id) {
                      handleGoToStore();
                    }
                  }}
                >
                  <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
                    <img
                      src={
                        store.img
                          ? getCDNResizedImageUrl(store.img, {
                              width: 400,
                              height: 300,
                            })
                          : "https://mahi-img.s3.ap-northeast-2.amazonaws.com/placeholder_store.png"
                      }
                      alt={store.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 bg-[#ff5f00] text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-sm">
                      NEW
                    </div>
                    {store.category && (
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[12px] font-bold px-2 py-1 rounded-md backdrop-blur-sm">
                        {store.category}
                      </div>
                    )}
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-bold text-lg truncate text-gray-900">
                      {store.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {store.address || "주소 정보 없음"}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="p-4 pt-6 flex flex-col gap-2">
          <button
            onClick={handleGoToStore}
            className="w-full h-12 bg-[#1562fc] rounded-xl text-white text-base font-bold shadow-md hover:bg-[#1150ce] transition-colors flex items-center justify-center gap-2"
          >
            매장 구경가기 🚀
          </button>
          <button
            onClick={onClose}
            className="w-full h-10 text-gray-400 text-sm font-medium hover:text-gray-600 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};
